import React, { useState } from 'react'
import UserAvatar from '../shared/UserAvatar'
import { getCommunityComments, deleteCommunityComment } from 'utils/teachingTeamApi'

function timeAgo(dateString) {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000)
  const intervals = [
    { label: 'y', seconds: 31536000 },
    { label: 'mo', seconds: 2592000 },
    { label: 'd', seconds: 86400 },
    { label: 'h', seconds: 3600 },
    { label: 'm', seconds: 60 },
  ]
  for (const { label, seconds: s } of intervals) {
    const count = Math.floor(seconds / s)
    if (count >= 1) return `${count}${label}`
  }
  return 'now'
}

function CommentItem({ comment, currentUser, isOwnerOrAdmin, onDeleted }) {
  const [deleting, setDeleting] = useState(false)
  const canDelete = currentUser && (isOwnerOrAdmin || currentUser.id === comment.author?.id)

  const handleDelete = async () => {
    if (!confirm('Delete this comment?')) return
    setDeleting(true)
    try {
      await deleteCommunityComment(comment.id)
      onDeleted && onDeleted(comment.id)
    } catch (err) {
      console.error('Failed to delete comment:', err)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex items-start gap-2 group">
      <UserAvatar
        src={comment.author?.avatar_url}
        name={comment.author?.name}
        size="xs"
        fallbackColor="#3366FF"
      />
      <div className="flex-1 min-w-0">
        <div className="inline-flex flex-col bg-gray-100 rounded-2xl px-3 py-2 max-w-full">
          <span className="text-xs font-semibold text-gray-800">{comment.author?.name}</span>
          <span className="text-sm text-gray-700 break-words">{comment.body}</span>
        </div>
        <div className="flex items-center gap-3 mt-0.5 ml-1">
          <span className="text-xs text-gray-400">{timeAgo(comment.created_at)}</span>
          {canDelete && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function CommunityCommentList({
  postId,
  teamId,
  recentComments = [],
  totalCount = 0,
  currentUser,
  isOwnerOrAdmin,
  onCommentDeleted,
  translations = {},
}) {
  const [allComments, setAllComments] = useState(null)
  const [loading, setLoading] = useState(false)

  const displayedComments = allComments ?? recentComments
  const showViewAll = !allComments && totalCount > recentComments.length

  const handleViewAll = async () => {
    if (loading) return
    setLoading(true)
    try {
      const data = await getCommunityComments(teamId, postId)
      setAllComments(data.comments || data)
    } catch (err) {
      console.error('Failed to load comments:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleted = (commentId) => {
    if (allComments) {
      setAllComments((prev) => prev.filter((c) => c.id !== commentId))
    }
    onCommentDeleted && onCommentDeleted(commentId)
  }

  if (!displayedComments.length && totalCount === 0) return null

  return (
    <div className="space-y-2 pt-2">
      {showViewAll && (
        <button
          onClick={handleViewAll}
          disabled={loading}
          className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
        >
          {loading
            ? (translations.loading || 'Loading...')
            : (translations.view_all_comments || `View all ${totalCount} comments`).replace('%{count}', totalCount)}
        </button>
      )}
      {displayedComments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          currentUser={currentUser}
          isOwnerOrAdmin={isOwnerOrAdmin}
          onDeleted={handleDeleted}
        />
      ))}
    </div>
  )
}
